import { Button } from "@ai-teach/ui-web";
import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { UpdateSchoolFsrsSettingsDocument } from "../../../generated/graphql";

interface SchoolSettingsEditorProps {
  schoolId: string;
  schoolName: string;
  currentSettings: {
    maxNewCardsPerSession: number;
    maxReviewsPerSession: number;
    maxAttemptsPerCard: number;
    maxSessionDuration: number;
    minNewCardsPercentage: number;
    maxCardsPerLesson: number;
    leechThreshold: number;
    defaultNotificationTime: string;
    streakRemindersEnabled: boolean;
  };
  onUpdate: () => void;
}

export function SchoolSettingsEditor({
  schoolId,
  schoolName,
  currentSettings,
  onUpdate,
}: SchoolSettingsEditorProps) {
  const [updateSettings, { loading: updateLoading }] = useMutation(
    UpdateSchoolFsrsSettingsDocument
  );

  const [formData, setFormData] = useState(currentSettings);

  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    // Ensure all fields have valid numbers (handle null/undefined)
    setFormData({
      maxNewCardsPerSession: currentSettings.maxNewCardsPerSession ?? 0,
      maxReviewsPerSession: currentSettings.maxReviewsPerSession ?? 0,
      maxAttemptsPerCard: currentSettings.maxAttemptsPerCard ?? 0,
      maxSessionDuration: currentSettings.maxSessionDuration ?? 0,
      minNewCardsPercentage: currentSettings.minNewCardsPercentage ?? 0,
      maxCardsPerLesson: currentSettings.maxCardsPerLesson ?? 0,
      leechThreshold: currentSettings.leechThreshold ?? 0,
      defaultNotificationTime: currentSettings.defaultNotificationTime ?? "",
      streakRemindersEnabled: currentSettings.streakRemindersEnabled ?? false,
    });
  }, [currentSettings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveMessage(null);

    try {
      // Remove __typename from formData before sending to mutation
      const { __typename, ...settingsInput } = formData as typeof formData & {
        __typename?: string;
      };

      console.log("Submitting school settings:", { schoolId, settings: settingsInput });

      const result = await updateSettings({
        variables: {
          schoolId,
          settings: settingsInput,
        },
      });

      console.log("Update result:", result);
      setSaveMessage({ type: "success", text: "School settings updated successfully" });

      // Call onUpdate after a short delay to ensure success message is visible
      setTimeout(() => {
        onUpdate();
      }, 100);

      // Auto-dismiss success message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    } catch (err) {
      console.error("Error updating school settings:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : typeof err === "object" && err !== null
            ? JSON.stringify(err)
            : "Failed to update settings";
      setSaveMessage({
        type: "error",
        text: errorMessage,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="panel p-6">
      <h3 className="h5 mb-6">FSRS Settings for {schoolName}</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block label mb-2">Max New Cards Per Session</label>
          <input
            type="number"
            value={formData.maxNewCardsPerSession}
            onChange={(e) =>
              setFormData({
                ...formData,
                maxNewCardsPerSession: Number.parseInt(e.target.value) || 0,
              })
            }
            className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Max Reviews Per Session</label>
          <input
            type="number"
            value={formData.maxReviewsPerSession}
            onChange={(e) =>
              setFormData({
                ...formData,
                maxReviewsPerSession: Number.parseInt(e.target.value) || 0,
              })
            }
            className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Max Attempts Per Card</label>
          <input
            type="number"
            value={formData.maxAttemptsPerCard}
            onChange={(e) =>
              setFormData({
                ...formData,
                maxAttemptsPerCard: Number.parseInt(e.target.value) || 0,
              })
            }
            className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Max Session Duration (minutes)</label>
          <input
            type="number"
            value={formData.maxSessionDuration}
            onChange={(e) =>
              setFormData({
                ...formData,
                maxSessionDuration: Number.parseInt(e.target.value) || 0,
              })
            }
            className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Min New Cards Percentage</label>
          <input
            type="number"
            value={formData.minNewCardsPercentage}
            onChange={(e) =>
              setFormData({
                ...formData,
                minNewCardsPercentage: Number.parseInt(e.target.value) || 0,
              })
            }
            className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            min="0"
            max="100"
          />
        </div>

        <div>
          <label className="block label mb-2">Max Cards Per Lesson</label>
          <input
            type="number"
            value={formData.maxCardsPerLesson}
            onChange={(e) =>
              setFormData({
                ...formData,
                maxCardsPerLesson: Number.parseInt(e.target.value) || 0,
              })
            }
            className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Leech Threshold</label>
          <input
            type="number"
            value={formData.leechThreshold}
            onChange={(e) =>
              setFormData({
                ...formData,
                leechThreshold: Number.parseInt(e.target.value) || 0,
              })
            }
            className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Default Notification Time</label>
          <input
            type="time"
            value={formData.defaultNotificationTime}
            onChange={(e) => setFormData({ ...formData, defaultNotificationTime: e.target.value })}
            className="w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 [color-scheme:dark] picker-teal"
          />
        </div>

        <div className="md:col-span-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.streakRemindersEnabled}
              onChange={(e) =>
                setFormData({ ...formData, streakRemindersEnabled: e.target.checked })
              }
              className="w-4 h-4 text-primary-600 bg-surface border-gray-800 rounded focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-secondary-200">Enable Streak Reminders</span>
          </label>
        </div>
      </div>

      {saveMessage && (
        <div
          className={`mt-6 p-4 rounded-md border ${
            saveMessage.type === "success"
              ? "bg-success-500/10 border-success-500/20 text-success-500"
              : "bg-error-500/10 border-error-500/20 text-error-500"
          }`}
        >
          {saveMessage.text}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button type="submit" variant="primary" disabled={updateLoading}>
          {updateLoading ? "Saving..." : "Save School Settings"}
        </Button>
      </div>
    </form>
  );
}
